import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronUp, RefreshCw } from 'lucide-react';
import { SectionHeader } from '../../components/ui';
import api from '../../api/client';
import { istLocalToUtcIso } from './istToUtcIso';

const TIMEFRAMES = ['1m', '3m', '5m', '15m', '1h'];

const NUM_COLS = ['open', 'high', 'low', 'close', 'volume'];

export function fetchCandles(filters) {
  const params = { symbol: filters.symbol, timeframe: filters.timeframe };
  const fromIso = istLocalToUtcIso(filters.from);
  const toIso = istLocalToUtcIso(filters.to);
  if (fromIso) params.from = fromIso;
  if (toIso)   params.to   = toIso;
  return api.get('/admin/candles', { params });
}

export default function AdminCandleInspector() {
  const [draft, setDraft] = useState({ symbol: '', timeframe: '1m', from: '', to: '' });
  const [filters, setFilters] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [expandedIdx, setExpandedIdx] = useState(null);

  const { data, isLoading, isFetching, isError, error } = useQuery({
    queryKey: ['adminCandles', filters],
    queryFn: () => fetchCandles(filters),
    enabled: !!filters?.symbol,
    staleTime: 0,
    retry: false,
  });

  const candles = Array.isArray(data) ? data : (data?.candles ?? []);

  const sorted = useMemo(() => {
    if (!candles.length) return candles;
    return [...candles].sort((a, b) =>
      sortDir === 'asc'
        ? String(a.candle_start).localeCompare(String(b.candle_start))
        : String(b.candle_start).localeCompare(String(a.candle_start))
    );
  }, [candles, sortDir]);

  function handleLoad() {
    if (!draft.symbol.trim()) return;
    setExpandedIdx(null);
    setFilters({ ...draft, symbol: draft.symbol.trim().toUpperCase() });
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter') handleLoad();
  }

  const loading = isLoading || isFetching;

  return (
    <div className="max-w-6xl space-y-4">
      <SectionHeader
        title="Candle Inspector"
        subtitle="Query and verify raw OHLCV candle data"
        actions={
          filters && (
            <button
              onClick={handleLoad}
              disabled={loading}
              className="btn-ghost flex items-center gap-1.5 text-xs"
            >
              <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
              Refresh
            </button>
          )
        }
      />

      {/* ── Filter bar ── */}
      <div className="panel p-3 flex flex-wrap items-end gap-3">
        {/* Symbol */}
        <div>
          <div className="field-label mb-0.5">Symbol</div>
          <input
            type="text"
            placeholder="e.g. NIFTY50"
            value={draft.symbol}
            onChange={(e) => setDraft((d) => ({ ...d, symbol: e.target.value }))}
            onKeyDown={handleKeyDown}
            className="bg-bg-card border border-bg-border rounded px-2 py-1 text-xs font-mono text-txt-primary w-36 focus:outline-none focus:border-amber/40"
          />
        </div>

        {/* Timeframe */}
        <div>
          <div className="field-label mb-0.5">Timeframe</div>
          <div className="relative">
            <select
              value={draft.timeframe}
              onChange={(e) => setDraft((d) => ({ ...d, timeframe: e.target.value }))}
              className="bg-bg-card border border-bg-border rounded px-2 py-1 text-xs font-mono text-txt-primary appearance-none pr-6 cursor-pointer"
            >
              {TIMEFRAMES.map((tf) => (
                <option key={tf} value={tf}>{tf}</option>
              ))}
            </select>
            <ChevronDown size={11} className="absolute right-1.5 top-1/2 -translate-y-1/2 pointer-events-none text-txt-muted" />
          </div>
        </div>

        {/* From */}
        <div>
          <div className="field-label mb-0.5">From (IST)</div>
          <input
            type="datetime-local"
            value={draft.from}
            onChange={(e) => setDraft((d) => ({ ...d, from: e.target.value }))}
            className="bg-bg-card border border-bg-border rounded px-2 py-1 text-xs font-mono text-txt-primary focus:outline-none focus:border-amber/40"
          />
        </div>

        {/* To */}
        <div>
          <div className="field-label mb-0.5">To (IST)</div>
          <input
            type="datetime-local"
            value={draft.to}
            onChange={(e) => setDraft((d) => ({ ...d, to: e.target.value }))}
            className="bg-bg-card border border-bg-border rounded px-2 py-1 text-xs font-mono text-txt-primary focus:outline-none focus:border-amber/40"
          />
        </div>

        <button
          onClick={handleLoad}
          disabled={!draft.symbol.trim() || loading}
          className="btn-primary text-xs px-4 py-1.5"
        >
          {loading ? 'Loading…' : 'Load'}
        </button>
      </div>

      {/* ── Table ── */}
      <div className="panel p-0 overflow-hidden">
        {/* Status bar */}
        {!filters && (
          <p className="text-xs text-txt-muted font-mono px-4 py-6 text-center">
            Enter a symbol and click Load to inspect candles.
          </p>
        )}

        {filters && isError && (
          <p className="text-xs text-bear font-mono px-4 py-4">
            {error?.response?.data?.detail || error?.message || 'Failed to load candles.'}
          </p>
        )}

        {filters && !isError && (
          <>
            {/* Row count */}
            {!loading && sorted.length > 0 && (
              <div className="px-4 py-2 border-b border-bg-border/40 flex items-center justify-between">
                <span className="text-2xs font-mono text-txt-muted">
                  {sorted.length} candle{sorted.length !== 1 ? 's' : ''} — {filters.symbol} {filters.timeframe}
                </span>
              </div>
            )}

            <div className="overflow-y-auto max-h-[60vh]">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-bg-panel z-10">
                  <tr className="border-b border-bg-border">
                    {/* Sortable time column */}
                    <th
                      onClick={() => setSortDir((d) => d === 'asc' ? 'desc' : 'asc')}
                      className="text-left py-2 px-3 text-2xs font-mono text-txt-muted uppercase tracking-widest cursor-pointer hover:text-amber transition-colors select-none whitespace-nowrap"
                    >
                      Time {sortDir === 'asc' ? '↑' : '↓'}
                    </th>
                    {NUM_COLS.map((col) => (
                      <th
                        key={col}
                        className="text-right py-2 px-3 text-2xs font-mono text-txt-muted uppercase tracking-widest"
                      >
                        {col}
                      </th>
                    ))}
                    <th className="w-6" />
                  </tr>
                </thead>

                <tbody>
                  {loading && (
                    <tr>
                      <td colSpan={7} className="py-8 text-center">
                        <span className="text-xs text-txt-muted font-mono animate-pulse">Loading…</span>
                      </td>
                    </tr>
                  )}

                  {!loading && sorted.length === 0 && (
                    <tr>
                      <td colSpan={7} className="py-8 text-center text-xs font-mono text-txt-muted">
                        No candles found.
                      </td>
                    </tr>
                  )}

                  {!loading && sorted.map((candle, i) => (
                    <>
                      <tr
                        key={i}
                        onClick={() => setExpandedIdx(expandedIdx === i ? null : i)}
                        className="border-b border-bg-border/30 hover:bg-bg-hover transition-colors cursor-pointer"
                      >
                        <td className="py-2 px-3 font-mono text-txt-secondary whitespace-nowrap">
                          {candle.candle_start}
                        </td>
                        {NUM_COLS.map((col) => (
                          <td key={col} className="py-2 px-3 font-mono text-right tabular-nums text-txt-primary">
                            {candle[col] != null ? candle[col] : '—'}
                          </td>
                        ))}
                        <td className="py-2 px-2 text-txt-muted">
                          {expandedIdx === i
                            ? <ChevronUp size={12} />
                            : <ChevronDown size={12} />
                          }
                        </td>
                      </tr>

                      {expandedIdx === i && (
                        <tr key={`${i}-detail`} className="bg-bg-surface border-b border-bg-border/30">
                          <td colSpan={7} className="px-4 py-3">
                            <pre className="text-2xs font-mono text-txt-muted whitespace-pre-wrap leading-relaxed">
                              {JSON.stringify(candle, null, 2)}
                            </pre>
                          </td>
                        </tr>
                      )}
                    </>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
